/**
 * src/components/AboutPage.tsx
 * About Us page — mirrors PrivacyPage/TermsPage structure
 */
import React from 'react';

export default function AboutPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', color: '#111827' }}>
      {/* ── NAV ── */}
      <header style={{
        background: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '0 16px',
        height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img src="/logo-icon.png" alt="CloudAIRambo logo" style={{ width: 34, height: 34, objectFit: 'contain' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontWeight: 700, fontSize: '18px', color: '#111827', letterSpacing: '-0.01em' }}>CloudAIRambo</span>
        </a>
        <a href="/" style={{ fontSize: '14px', color: '#6b7280', textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
          onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
        >← Back to home</a>
      </header>

      {/* ── HERO ── */}
      <div style={{ textAlign: 'center', padding: '64px 24px 48px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
        <img src="/logo.png" alt="CloudAIRambo" style={{ height: 80, width: 'auto', objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: '#f0f4ff', border: '1px solid #c7d4fe', borderRadius: '999px',
          padding: '5px 14px', fontSize: '12px', color: '#4f46e5', fontWeight: 600, marginBottom: '24px',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />
          About Us
        </div>
        <h1 style={{ fontSize: 'clamp(28px, 8vw, 44px)', fontWeight: 800, color: '#111827', margin: '0 0 12px', letterSpacing: '-0.02em' }}>
          About CloudAIRambo
        </h1>
        <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 auto', maxWidth: '580px', lineHeight: 1.6 }}>
          One platform to invoice, take payments, run point-of-sale, and manage a team — built for
          businesses that don't have time to fight with software.
        </p>
      </div>

      {/* ── BODY ── */}
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '56px 32px 96px', fontSize: '15px', lineHeight: 1.75, color: '#374151' }}>

        <Section title="Who We Are">
          <p>
            CloudAIRambo is a business management platform for small and growing businesses. Under one
            login, it covers invoicing and billing, point-of-sale, inventory, staff attendance and payroll
            prep, a client/customer ledger, and add-on modules for specific business types — gyms,
            clinics/queues, and canteens, among others.
          </p>
          <p>
            We built it because most business software forces a choice: tools simple enough for a small
            team to actually set up themselves, or tools powerful enough to run a real business on —
            rarely both. CloudAIRambo is our attempt to close that gap.
          </p>
        </Section>

        <Section title="Fully Remote, By Design">
          <p>
            CloudAIRambo operates as a fully remote, distributed team with no physical office. This isn't
            a stopgap — it's a deliberate choice that keeps us lean and lets us ship faster for the
            businesses we serve.
          </p>
          <p>
            We are based in India, and our team, infrastructure, and support all operate from here. We
            don't publish a street address publicly, since day-to-day support and communication happen
            entirely through the channels below rather than in person.
          </p>
        </Section>

        <Section title="What We Build">
          <p>
            CloudAIRambo brings together the tools a business normally has to piece together from several
            different apps, under one login:
          </p>
          <ul style={{ paddingLeft: '20px', margin: '0 0 12px' }}>
            <li style={{ marginBottom: '8px' }}><strong>Invoicing</strong> — a drag-and-drop invoice creator, quick invoice for one-off bills, batch invoice generator for bulk runs, and a product catalog to pull line items from.</li>
            <li style={{ marginBottom: '8px' }}><strong>Point of sale & inventory</strong> — POS billing, barcode generation and scanning, and real-time inventory tracking.</li>
            <li style={{ marginBottom: '8px' }}><strong>Credit book</strong> — a running ledger for customer credit and dues, so you're not tracking "udhaar" in a notebook.</li>
            <li style={{ marginBottom: '8px' }}><strong>Queue & order management</strong> — token/queue handling, live order tracking, and order display screens for counters and waiting areas.</li>
            <li style={{ marginBottom: '8px' }}><strong>Attendance & payroll</strong> — face recognition and barcode-based attendance, feeding into leave tracking and payroll prep.</li>
            <li><strong>Industry modules</strong> — purpose-built flows for gyms and dining/canteen operations, each with their own check-in, scheduling, and member management.</li>
          </ul>
          <p>
            Our goal is straightforward: give a business owner running a shop, a gym, a clinic, a canteen,
            or a small team the same quality of tooling that larger companies take for granted — without
            the cost or complexity that usually comes with it.
          </p>
        </Section>

        <Section title="Get in Touch">
          <div style={{
            background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '14px',
            padding: '24px 28px', marginTop: '12px',
          }}>
            <p style={{ margin: 0 }}>
              Questions, feedback, or just want to say hi? Reach us at{' '}
              <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 600 }}>
                support@cloudairambo.com
              </a>
            </p>
          </div>
        </Section>

      </div>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
        <div style={{
          maxWidth: '1100px', margin: '0 auto', padding: '24px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: '13px', color: '#374151' }}>© 2026 CloudAIRambo. All rights reserved.</span>
          <div style={{ display: 'flex', gap: 16 }}>
            <a href="/privacy" style={{ fontSize: '13px', color: '#4b5563', textDecoration: 'none', fontWeight: 500 }}>Privacy</a>
            <a href="/terms" style={{ fontSize: '13px', color: '#4b5563', textDecoration: 'none', fontWeight: 500 }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: '40px' }}>
      <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#111827', marginBottom: '14px' }}>{title}</h2>
      {children}
    </section>
  );
}
